import { useRef, useState, type FormEvent } from 'react';
import type { CallerIdInfo } from '../api';
import { ROTATE, pickerValue } from '../phone/caller-id';
import { cleanTyped, formatPhone, normalizeNumber } from '../phone/number';
import { Keypad } from './Keypad';
import { BackspaceIcon, PhoneIcon } from './icons';
import { fullLabel } from '../phone/number-names';

export function Dialer({
  value,
  onChange,
  canCall,
  disabledReason,
  onCall,
  callerId,
  onChooseCallerId,
}: {
  value: string;
  onChange: (v: string) => void;
  canCall: boolean;
  disabledReason: string | null;
  onCall: (number: string) => void;
  callerId: CallerIdInfo | null;
  onChooseCallerId: (choice: string) => Promise<string | null>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const number = normalizeNumber(value);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (canCall && number) onCall(number);
  };

  const press = (k: string) => {
    if (k === '+' && value.trim()) return; // + only at the start
    onChange(cleanTyped(value + k));
    input.current?.focus({ preventScroll: true });
  };

  return (
    <form className="dialer" onSubmit={submit}>
      <CallingFrom info={callerId} onChoose={onChooseCallerId} />
      <div className="display">
        <input
          ref={input}
          className="display-input"
          name="number"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          placeholder="Enter a number"
          aria-label="Number to call"
          value={value}
          onChange={(e) => onChange(cleanTyped(e.target.value))}
        />
        {value && (
          <button
            type="button"
            className="icon-btn"
            aria-label="Delete last digit"
            onClick={() => onChange(value.slice(0, -1))}
            onDoubleClick={() => onChange('')}
          >
            <BackspaceIcon />
          </button>
        )}
      </div>

      <Keypad onKey={press} />

      <button className="call-btn" type="submit" disabled={!canCall || !number} aria-label={number ? `Call ${number}` : 'Call'}>
        <PhoneIcon size={26} />
      </button>
      <p className="dialer-hint">
        {!canCall && disabledReason
          ? disabledReason
          : 'Type or paste a number. Try *43 for an echo test.'}
      </p>
    </form>
  );
}

/**
 * "Calling from" above the number, like Zoom's caller ID: one number as text,
 * several as a menu (plus "Rotate", each call taking the next number).
 */
export function CallingFrom({ info, onChoose }: { info: CallerIdInfo | null; onChoose: (choice: string) => Promise<string | null> }) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  if (!info) return <div className="from" aria-hidden />;
  const several = info.numbers.length > 1;
  const shown = info.number ?? info.numbers[0] ?? null;
  if (!several && !shown) return <div className="from" aria-hidden />;

  const change = async (choice: string) => {
    setSaving(true);
    setError(null);
    const err = await onChoose(choice);
    setSaving(false);
    setError(err);
  };

  return (
    <div className="from">
      {several ? (
        <label className="from-row">
          <span className="from-label">Calling from</span>
          <span className="from-pick">
            <select
              className="from-select"
              name="calling-from"
              value={pickerValue(info)}
              disabled={saving}
              onChange={(e) => void change(e.target.value)}
              aria-describedby={error ? 'from-error' : undefined}
            >
              {info.numbers.map((n) => (
                <option key={n} value={n}>
                  {fullLabel(n, info.names)}
                </option>
              ))}
              <option value={ROTATE} disabled={!info.ready}>
                {info.ready ? 'Rotate my numbers' : 'Rotate my numbers (not available now)'}
              </option>
            </select>
            <svg className="from-chevron" viewBox="0 0 12 12" width="12" height="12" aria-hidden>
              <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </label>
      ) : (
        <p className="from-row">
          <span className="from-label">Calling from</span>
          <span className="from-number">{shown ? fullLabel(shown, info.names) : ''}</span>
        </p>
      )}
      {info.mode === 'rotate' && !error && <p className="from-help">Each call uses your next number.</p>}
      {error && (
        <p className="from-error" id="from-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
